import type { Container, FederatedPointerEvent, PointData, Renderer, Sprite } from 'pixi.js';
import { Point, Rectangle, Ticker } from 'pixi.js';
import { Signal } from 'typed-signals';
import { clamp } from '@softgames/math-utils';
import { linear } from '../libs/TweenManager/interpolations';

interface ScrollRange {
  min: number;
  max: number;
}

export enum Direction {
  Vertical = 'Vertical',
  Horizontal = 'Horizontal',
}

export type DragBehaviorConfig = {
  horizontalScroll?: ScrollRange;
  verticalScroll?: ScrollRange;
  target: Container;
  inputLayer: Container | Sprite;
  renderer: Renderer;
  // FIX: without rubber banding scroll limits are not working
  rubberBanding?: boolean;
  velocityDrag?: number;
  rubberBandDrag?: number;
  rubberBandLerp?: number;
  rubberBandMargin?: number;
  pointerToVelocityThreshold?: number;
  wheelDirection?: Direction;
  initScrollThreshold?: number;
};

const defaultConfig = {
  velocityDrag: 0.9,
  rubberBandDrag: 0.5,
  rubberBandLerp: 0.5,
  rubberBandMargin: 100,
  pointerToVelocityThreshold: 3,
  initScrollThreshold: 0,
};

export default class ScrollBehaviour {
  public readonly onHorizontalScroll = new Signal<(delta: number) => void>();

  public readonly onVerticalScroll = new Signal<(delta: number) => void>();

  public readonly onTargetPositionChanged = new Signal();

  public readonly onScrollEnd = new Signal();

  private readonly config: DragBehaviorConfig;

  private readonly lastPointerPosition = new Point();

  private readonly downPosition = new Point();

  private readonly velocity = new Point();

  private readonly rubberBanding: boolean;

  private isDown = false;

  private active = true;

  private readonly lastTargetPosition: PointData = { x: 0, y: 0 };

  constructor(config: DragBehaviorConfig) {
    this.config = { ...defaultConfig, ...config };

    this.rubberBanding = config.rubberBanding ?? false;

    config.inputLayer.on('pointerdown', this.handlePointerDown);
    config.inputLayer.on('pointermove', this.handlePointerMove);
    config.inputLayer.on('pointerup', this.handlePointerUp);
    config.inputLayer.on('pointerupoutside', this.handlePointerUp);
    if (config.wheelDirection) this.initWheel();

    const ticker = new Ticker();
    ticker.start();

    ticker.add(this.update);

    this.lastTargetPosition.x = this.config.target.x;
    this.lastTargetPosition.y = this.config.target.y;

    this.config.target.once('destroyed', () => {
      ticker.remove(this.update);
      ticker.destroy();
    });
  }

  public getConfig(): DragBehaviorConfig {
    return this.config;
  }

  public clampPoint(point: Point): Point {
    const { range: rangeH } = this.getDirectionData(Direction.Horizontal);
    const { range: rangeV } = this.getDirectionData(Direction.Vertical);

    if (rangeH) point.x = clamp(point.x, rangeH.min, rangeH.max);
    if (rangeV) point.y = clamp(point.y, rangeV.min, rangeV.max);

    return point;
  }

  public updateConfig(
    update: Pick<DragBehaviorConfig, 'horizontalScroll' | 'verticalScroll' | 'wheelDirection'>,
  ): void {
    Object.assign(this.config, update);
  }

  public setActive(active: boolean): void {
    this.active = active;
    this.isDown = false;
  }

  public async scrollToTop(duration = 800): Promise<void> {
    if (!this.config.verticalScroll) return;
    await this.config.target
      .tweenTo({ y: this.config.verticalScroll.max }, duration, 'cubicOut', true)
      .asyncOnComplete();
  }

  private initWheel(): void {
    if (!(this.config.inputLayer.hitArea instanceof Rectangle)) {
      throw new Error('DragBehaviour - wheel listener requires rectangle as hitArea of inputLayer');
    }

    window.addEventListener('wheel', this.handleWheel);
    this.config.inputLayer.once('destroyed', () => {
      window.removeEventListener('wheel', this.handleWheel);
    });
  }

  private readonly handleWheel = (e: WheelEvent): void => {
    if (!this.active || !this.config.wheelDirection || !this.config.target.visible) return;

    if (!this.isPointerOverInputLayer(e)) return;

    const direction = this.config.wheelDirection;

    const invertedDelta = (direction === Direction.Vertical ? e.deltaY : e.deltaX || e.deltaY) * -1;

    if (this.shouldIgnoreScroll(direction, invertedDelta)) return;

    if (direction === Direction.Vertical) {
      this.velocity.set(0, invertedDelta);
      this.onVerticalScroll.emit(invertedDelta);
    } else {
      this.velocity.set(invertedDelta, 0);
      this.onHorizontalScroll.emit(invertedDelta);
    }
  };

  private isPointerOverInputLayer(domPointer: { x: number; y: number }): boolean {
    const point = new Point();

    this.config.renderer.events.mapPositionToPoint(point, domPointer.x, domPointer.y);

    const globalPos = this.config.inputLayer.getGlobalPosition();
    const hitArea = this.config.inputLayer.hitArea as Rectangle;
    const rect = new Rectangle(globalPos.x + hitArea.x, globalPos.y + hitArea.y, hitArea.width, hitArea.height);
    return rect.contains(point.x, point.y);
  }

  private readonly handlePointerDown = (e: FederatedPointerEvent): void => {
    if (!this.active) return;
    this.isDown = true;
    this.downPosition.set(e.global.x, e.global.y);
    this.lastPointerPosition.set(e.global.x, e.global.y);
  };

  private readonly handlePointerMove = (e: FederatedPointerEvent): void => {
    if (!this.active) return;
    if (!this.isDown) return;

    const { pointerToVelocityThreshold = defaultConfig.pointerToVelocityThreshold } = this.config;

    if (this.config.initScrollThreshold) {
      let diff = 0;
      if (this.config.horizontalScroll) {
        diff = Math.abs(this.downPosition.x - e.global.x);
      } else if (this.config.verticalScroll) {
        diff = Math.abs(this.downPosition.y - e.global.y);
      }

      if (diff < this.config.initScrollThreshold) {
        this.lastPointerPosition.set(e.global.x, e.global.y);
        return;
      }
    }

    const diffX = e.global.x - this.lastPointerPosition.x;
    const diffY = e.global.y - this.lastPointerPosition.y;
    this.lastPointerPosition.set(e.global.x, e.global.y);
    this.velocity.set(
      Math.abs(diffX) > pointerToVelocityThreshold ? diffX : 0,
      Math.abs(diffY) > pointerToVelocityThreshold ? diffY : 0,
    );
    this.applyMove(Direction.Horizontal, diffX);
    this.applyMove(Direction.Vertical, diffY);
  };

  private readonly handlePointerUp = (): void => {
    if (!this.active) return;
    if (!this.isDown) return;
    this.isDown = false;
    this.onScrollEnd.emit();
  };

  private readonly update = (): void => {
    if (!this.active) return;

    if (!this.isDown) {
      if (this.rubberBanding) {
        this.updateRubberBand(Direction.Vertical);
        this.updateRubberBand(Direction.Horizontal);
      }
      this.applyVelocity(Direction.Vertical);
      this.applyVelocity(Direction.Horizontal);
    }
    const { target } = this.config;
    if (this.lastTargetPosition.x !== target.x || this.lastTargetPosition.y !== target.y) {
      this.onTargetPositionChanged.emit();
      this.lastTargetPosition.x = target.x;
      this.lastTargetPosition.y = target.y;
    }
  };

  private applyRubberBandingToDiff(direction: Direction, delta: number): number {
    const { target, rubberBandMargin = defaultConfig.rubberBandMargin } = this.config;
    const { prop, range } = this.getDirectionData(direction);
    if (!range) return delta;

    const { min, max } = range;
    const currentPos = target[prop];

    const rubberFriction =
      Math.max(Math.abs(Math.min(currentPos - min, 0)), Math.max(currentPos - max, 0)) / rubberBandMargin;
    return delta * (1 - Math.abs(rubberFriction));
  }

  private applyMove(direction: Direction, delta: number): void {
    const { prop, range } = this.getDirectionData(direction);
    if (!range) return;
    const { target } = this.config;
    if (direction === Direction.Horizontal) {
      this.onHorizontalScroll.emit(delta);
    } else {
      this.onVerticalScroll.emit(delta);
    }
    target[prop] += this.rubberBanding ? this.applyRubberBandingToDiff(direction, delta) : delta;
  }

  private applyVelocity(direction: Direction): void {
    const { velocityDrag = defaultConfig.velocityDrag } = this.config;
    const { prop, range } = this.getDirectionData(direction);
    if (!range) return;
    this.velocity[prop] *= velocityDrag;
    this.config.target[prop] += this.velocity[prop];
    if (Math.abs(this.velocity[prop]) < 0.01) this.velocity[prop] = 0;
  }

  private shouldIgnoreScroll(direction: Direction, delta: number): boolean {
    const { prop, range } = this.getDirectionData(direction);
    if (!range) return false;

    const { target } = this.config;
    const { min, max } = range;

    return (target[prop] <= min && delta < 0) || (target[prop] >= max && delta > 0);
  }

  private updateRubberBand(direction: Direction): void {
    const { prop, range } = this.getDirectionData(direction);
    if (!range) return;

    const { target, rubberBandDrag, rubberBandLerp } = this.config;
    const { min, max } = range;

    const currentPos = target[prop];
    if (currentPos < min || currentPos > max) {
      this.velocity[prop] *= rubberBandDrag ?? 1;
      target[prop] = linear(currentPos, currentPos < min ? min : max, rubberBandLerp ?? 0.5);
    }
  }

  private getDirectionData(direction: Direction): {
    prop: 'x' | 'y';
    range: ScrollRange | undefined;
  } {
    return {
      prop: direction === Direction.Vertical ? 'y' : 'x',
      range: direction === Direction.Vertical ? this.config.verticalScroll : this.config.horizontalScroll,
    };
  }
}
